import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import { useGetChainRatePlans } from '~/hooks/apis/reservation/useChainRatePlans';
import type { ChainRatePlanGroup, RatePlanStatus } from '~/hooks/apis/reservation/useChainRatePlans';
import useDebounced from '~/hooks/useDebounced';

import DraftRatePlanReview from './DraftRatePlanReview';

interface Props {
  chainSfid: string;
}

const PER_PAGE = 50;
const DEBOUNCE_MS = 400;

const headerCellSx = {
  fontWeight: 500,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'text.secondary',
} as const;

function completionChip(group: ChainRatePlanGroup) {
  const { complete_count, property_count } = group;
  if (complete_count === property_count) {
    return { label: `✓ ${complete_count}/${property_count}`, color: 'success' as const };
  }
  if (complete_count === 0) {
    return { label: `✗ 0/${property_count}`, color: 'error' as const };
  }
  return { label: `◑ ${complete_count}/${property_count}`, color: 'warning' as const };
}

export default function ChainRatePlansTab({ chainSfid }: Props) {
  const [statusTab, setStatusTab] = useState<RatePlanStatus>('content-approved');
  const [page, setPage] = useState(1);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');

  const debouncedCodeFilter = useDebounced(codeFilter, DEBOUNCE_MS);
  const debouncedPropertyFilter = useDebounced(propertyFilter, DEBOUNCE_MS);

  const prevFilters = useRef({ debouncedCodeFilter, debouncedPropertyFilter });
  useEffect(() => {
    if (
      prevFilters.current.debouncedCodeFilter !== debouncedCodeFilter ||
      prevFilters.current.debouncedPropertyFilter !== debouncedPropertyFilter
    ) {
      setPage(1);
      prevFilters.current = { debouncedCodeFilter, debouncedPropertyFilter };
    }
  }, [debouncedCodeFilter, debouncedPropertyFilter]);

  const handleStatusTabChange = useCallback((_: SyntheticEvent, value: RatePlanStatus) => {
    setStatusTab(value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  }, []);

  const handleReset = useCallback(() => {
    setCodeFilter('');
    setPropertyFilter('');
  }, []);

  const { ratePlanGroups, totalCount, isLoading, error } = useGetChainRatePlans([chainSfid], {
    status: statusTab,
    page,
    limit: PER_PAGE,
    ratePlanCode: debouncedCodeFilter || undefined,
    propertySearch: debouncedPropertyFilter || undefined,
  });

  const pageCount = Math.ceil(totalCount / PER_PAGE);
  const groupedView = (
    <>
      {error && !isLoading && <Alert severity="error">Failed to load rate plans.</Alert>}
      {!isLoading && !error && ratePlanGroups.length === 0 && (
        <Box textAlign="center" py={8} color="text.secondary">
          <Typography>No rate plans found for this chain.</Typography>
        </Box>
      )}
      {ratePlanGroups.length > 0 && (
        <Paper variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ ...headerCellSx, whiteSpace: 'nowrap' }}>Rate Plan Code</TableCell>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Properties</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Completion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ratePlanGroups.map((group) => {
                const chip = completionChip(group);
                return (
                  <TableRow key={group.rate_plan_code} sx={{ '&:hover': { bgcolor: '#f5f9ff' } }}>
                    <TableCell>
                      <Typography
                        component="code"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: 'grey.200',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                          color: 'grey.800',
                        }}
                      >
                        {group.rate_plan_code ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'grey.800' }}>{group.name}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'right' }}>
                      {group.property_count}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Chip
                        label={chip.label}
                        size="small"
                        color={chip.color}
                        sx={{ height: 20, fontSize: 11, fontWeight: 600, borderRadius: 5 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination count={pageCount} page={page} onChange={handlePageChange} color="primary" />
        </Box>
      )}
    </>
  );

  return (
    <Stack spacing={2}>
      {/* ── Filters ── */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          Filter rates
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="flex-end">
          <TextField
            size="small"
            label="Property ID / Name"
            placeholder="e.g. Crowne Plaza"
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            sx={{ minWidth: 220, flexGrow: 1 }}
          />
          <TextField
            size="small"
            label="Rate Plan Code"
            placeholder="e.g. FLEXBB25"
            value={codeFilter}
            onChange={(e) => setCodeFilter(e.target.value)}
            sx={{ minWidth: 180, flexGrow: 1 }}
          />
          <Button variant="outlined" size="small" onClick={handleReset} sx={{ height: 40 }}>
            Reset
          </Button>
        </Stack>
      </Paper>

      {/* ── Status sub-tabs ── */}
      <Tabs value={statusTab} onChange={handleStatusTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab
          value="content-approved"
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>Content-Approved</span>
              {statusTab === 'content-approved' && totalCount > 0 && (
                <Chip label={totalCount} size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Stack>
          }
        />
        <Tab
          value="draft"
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>Draft</span>
            </Stack>
          }
        />
      </Tabs>

      {statusTab === 'draft' ? (
        <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR]} fallback={groupedView}>
          <DraftRatePlanReview
            key={chainSfid}
            chainSfid={chainSfid}
            filters={{
              page,
              limit: PER_PAGE,
              ratePlanCode: debouncedCodeFilter || undefined,
              propertySearch: debouncedPropertyFilter || undefined,
            }}
            onPageChange={setPage}
          />
        </PermissionedComponent>
      ) : (
        groupedView
      )}
    </Stack>
  );
}
